import type { SetlistContext } from './client';
import { STOCK_VIDEO_CATEGORIES } from './collections';
import type { VideoCategory } from './stock-video-types';
import { getCategoryStatusFilter } from './stock-video-types';
import { VECTOR_SEARCH_DEFAULTS } from './vector-search';

/**
 * Fetches categories similar to a given category using vector search.
 * Uses multi-search endpoint (POST) to avoid URL length limits with large embeddings.
 */
export const getSimilarCategories = async (
  context: SetlistContext,
  {
    categoryEmbedding,
    excludeCategoryId,
    limit = 12,
    distanceThreshold = VECTOR_SEARCH_DEFAULTS.CATEGORY.DISTANCE_THRESHOLD,
    k = VECTOR_SEARCH_DEFAULTS.CATEGORY.K,
    isAdmin = false,
  }: {
    categoryEmbedding: number[];
    excludeCategoryId: string;
    limit?: number;
    distanceThreshold?: number;
    k?: number;
    isAdmin?: boolean;
  }
): Promise<VideoCategory[]> => {
  // Use multi-search endpoint (POST) to handle large embedding vectors
  // When distance_threshold < 1.0, omit k parameter to let threshold do the filtering
  // When distance_threshold >= 1.0 (no filtering), use k to limit results
  const vectorParams =
    distanceThreshold < 1.0
      ? `distance_threshold:${distanceThreshold}`
      : `k:${k}`;

  const statusFilter = getCategoryStatusFilter(isAdmin);

  const multiSearchResponse = await context.client.multiSearch.perform(
    {
      searches: [
        {
          collection: STOCK_VIDEO_CATEGORIES,
          q: '*',
          vector_query: `embedding:([${categoryEmbedding.join(',')}], ${vectorParams})`,
          per_page: limit,
          prefix: false,
          sort_by: '_vector_distance:asc',
          filter_by: `id:!=${excludeCategoryId} && ${statusFilter}`,
        },
      ],
    },
    {},
    { cacheSearchResultsForSeconds: 60 }
  );

  const result = multiSearchResponse.results[0] as {
    hits?: Array<{
      document: VideoCategory;
    }>;
  };
  const categories = (result.hits ?? []).map((hit) => hit.document);

  return categories;
};
